import { useEffect, useLayoutEffect, useRef } from "react";
import type { RefObject } from "react";

export interface ReportPeriod {
  year: number;
  /** 1-12; omitted for reports that only filter by fiscal year (e.g. Leave Balance) */
  month?: number;
}

export interface ReportTabPanelProps {
  /** Report summary values for the shared StatCards row */
  onStatsChange?: (values: Record<string, unknown>) => void;
  /** Register (or clear, with null) the tab's export handler so the shared header Export button can call it */
  registerExport?: (handler: (() => void | Promise<void>) | null) => void;
  /** Period selected via the header Calendar picker; the tab applies it to its own filter state */
  period?: ReportPeriod;
}

/**
 * Registers an export handler with the Reports shell, always invoking the latest
 * handler closure without re-registering on every render. Uses a layout effect so
 * registration/cleanup run synchronously before paint: on a tab switch the old
 * tab's registration is cleared and the new tab's is applied in the same commit,
 * so the shell's handler always matches the active tab. Clears the registration
 * on unmount so the shell never points at a stale/unmounted tab.
 */
export function useRegisterExport(
  handler: (() => void | Promise<void>) | undefined,
  registerExport?: (handler: (() => void | Promise<void>) | null) => void,
): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useLayoutEffect(() => {
    if (!registerExport) return;
    registerExport(() => handlerRef.current?.());
    return () => registerExport(null);
  }, [registerExport]);
}

/**
 * Returns a ref that stays `true` while the component is mounted and flips to
 * `false` once it unmounts. Used to prevent late-async effects (e.g. a download
 * landing) from firing after the user has navigated to another tab.
 */
export function useMountedGuard(): RefObject<boolean> {
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  return mounted;
}